import React from 'react';
import { Button } from '../../../components';
import { Dropdown, Tooltip } from 'antd';
import {
    isApplianceAvailable,
    compareVersions,
    getSessionVar,
    isApplianceUpgradeableToVersion,
} from '../../../lib/helpers';
import {
    getCloudLogo,
    getApplianceReadiness,
    getMigrationApplianceURL,
    getApplianceDeploymentState,
    formatTags,
    canApplianceUpgrade,
    canTroubleshoot,
    canRedeploy,
    APPLIANCE_READINESS_STATE_INFO,
} from '../utils';
import { CONFIG } from '../../../constants';

import {
    EditOutlined,
    ProfileOutlined,
    DeleteOutlined,
    ToolOutlined,
    ArrowRightOutlined,
    MoreOutlined,
    InfoCircleOutlined,
    WarningOutlined,
} from '@ant-design/icons';

export const createTableColumns = ({
    handleOpenDeleteModal,
    handleUpgradeAppliance,
    handleOpenTroubleshootingModal,
    handleEditCloudAccount,
    handleRedeployAppliance,
}) => {
    // Action menu for each row
    const getActionMenu = (record) => {
        const items = [
            {
                key: 'upgrade',
                icon: <ArrowRightOutlined />,
                label: 'Upgrade Migration Appliance',
                disabled: !canApplianceUpgrade(record),
                onClick: () => handleUpgradeAppliance(record),
            },
            {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Edit Cloud Account',
                onClick: () => handleEditCloudAccount(record.id),
            },
            {
                key: 'redeploy',
                icon: <ProfileOutlined />,
                label: 'Redeploy Migration Appliance',
                disabled: !canRedeploy(record),
                onClick: () => handleRedeployAppliance(record.id),
            },
            {
                key: 'troubleshooting',
                icon: <ToolOutlined />,
                label: 'Network Troubleshooting',
                disabled: !canTroubleshoot(record),
                onClick: () => handleOpenTroubleshootingModal(record.id, record.name),
            },
            {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Remove Cloud Target Account',
                danger: true,
                disabled: !isApplianceAvailable(record),
                onClick: () => handleOpenDeleteModal(record.id, record.name),
            },
        ];

        return { items };
    };

    return [
        {
            title: 'Name',
            dataIndex: 'name',
            width: 250,
            key: 'name',
            ellipsis: {
                showTitle: false, // Disable default title, we'll use custom tooltip
            },
            render: (_, record) => {
                const logoSrc = `../../../img/${getCloudLogo(record.type)}?v=${process.env.BUILD_NUMBER}`;

                // Check if text would overflow (approximate calculation)
                const maxTextWidth = 250 - 32 - 16; // column width - logo width - margins/padding
                const isTextTruncated = record.name && record.name.length > maxTextWidth / 8; // rough character estimation

                const content = (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <img src={logoSrc} className="cloud-logo" style={{ marginRight: '8px' }} />
                        <span
                            style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {record.name}
                        </span>
                    </div>
                );

                return isTextTruncated ? <Tooltip title={record.name}>{content}</Tooltip> : content;
            },
        },
        {
            title: 'URL',
            dataIndex: 'url',
            render: (_, row) => getMigrationApplianceURL(row),
            key: 'url',
            width: 150,
            ellipsis: true,
        },
        {
            title: 'Tags',
            dataIndex: 'tags',
            className: 'tags-column cursor-pointer',
            key: 'tags',
            width: 150,
            ellipsis: true,
            render: (_, row) => {
                return (
                    <Tooltip title="Click to Add/Edit tags">
                        <span>{formatTags(row.tags)}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Appliance Deployment',
            width: 200,
            render: (_, row) => getApplianceDeploymentState(row.appliance),
            key: 'applianceDeployment',
        },
        {
            title: 'Appliance Readiness',
            width: 200,
            className: 'appliance-readiness',
            render: (_, row) => {
                return getApplianceReadiness(row, APPLIANCE_READINESS_STATE_INFO) ?? 'N/A';
            },
            key: 'applianceReadiness',
        },
        {
            title: 'Version',
            render: (_, row) => {
                const applianceVersionNum = row.appliance?.appliance_version_num;
                if (!applianceVersionNum) return '';

                const applianceLatestVersion = getSessionVar('applianceLatestVersion');
                const showWarning = compareVersions(applianceVersionNum, applianceLatestVersion) < 0;
                const versionTooOld = compareVersions(applianceVersionNum, CONFIG.CUTOFF_VERSION_FOR_UPGRADE_AVAIL) < 0;
                const versionUnUpgradeable = !isApplianceUpgradeableToVersion(
                    applianceVersionNum,
                    applianceLatestVersion,
                );

                return (
                    <span className="version-cell">
                        <span>{applianceVersionNum}</span>
                        {applianceVersionNum && (versionTooOld || versionUnUpgradeable) && (
                            <Tooltip
                                title={
                                    <span>
                                        A newer Migration Appliance version is available. Please{' '}
                                        <a
                                            target="_blank"
                                            href="http://docs.rivermeadow.com/redeploy-cloudappliance"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            redeploy
                                        </a>{' '}
                                        for the latest functionality.
                                    </span>
                                }
                                placement="right"
                            >
                                <WarningOutlined className="warning" onClick={(e) => e.stopPropagation()} />
                            </Tooltip>
                        )}
                        {applianceVersionNum && showWarning && !(versionTooOld || versionUnUpgradeable) && (
                            <Tooltip
                                title={
                                    <span>
                                        A newer Migration Appliance version is available. Please upgrade for the latest
                                        functionality. View{' '}
                                        <a
                                            target="_blank"
                                            href="http://docs.rivermeadow.com/upgrade-cloud-appliance"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Upgrade Migration Appliance
                                        </a>
                                        .
                                    </span>
                                }
                                placement="right"
                            >
                                <InfoCircleOutlined className="needinfo" onClick={(e) => e.stopPropagation()} />
                            </Tooltip>
                        )}
                    </span>
                );
            },
            key: 'version',
            width: 120,
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 75,
            align: 'center',
            render: (_, record) => {
                const handleMenuClick = (e) => {
                    e.domEvent.stopPropagation(); // Prevent row click

                    // Handle menu items
                    // const menuItems = getActionMenu(record).items;
                    // const clickedItem = menuItems.find(item => item.key === e.key);
                    // if (clickedItem && clickedItem.onClick) {
                    //     clickedItem.onClick();
                    // }
                };
                return (
                    <Dropdown
                        menu={{
                            ...getActionMenu(record),
                            onClick: handleMenuClick,
                        }}
                        trigger={['click']}
                    >
                        <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
                    </Dropdown>
                );
            },
        },
    ];
};
